<script>
  import moment from 'moment'
  import 'moment-timezone'
  import { DEFAULT_TIMEZONE, getDateFormat, getDateTimeFormat, US_COUNTRY } from '@base/configs'

  export default {
    methods: {
      /**
       * Format number based on market locale
       * US: 1,000.50
       * EU: 1.000,50 or 1 000,50
       * Format number based on market locale
       * @param {number} number - Number to format
       * @param {object} options - Formatting options
       * @param {number} options.precision - Decimal places (default: 2)
       * @param {boolean} options.acceptZero - Accept zero values (default: false)
       * @param {string} options.marketCountry - Market country code
       */
      formatNumber (number, options = {}) {
        const {
          precision = 2,
          acceptZero = false,
          marketCountry = null
        } = options

        // Reject everything that doesn't represent a usable number - null,
        // undefined, empty string, and anything that coerces to NaN. The
        // previous guard `number || (acceptZero && number !== null)` let
        // `undefined` slip through (rendering "NaN") and turned `""` into
        // "0,00" via `Number("") === 0`.
        if (number === null || number === undefined || number === '') return ''
        // Use parseNumber (not Number()) so locale-formatted strings like
        // "20,00" from a Danish-serialized API response are accepted too.
        const num = typeof number === 'number' ? number : this.parseNumber(number)
        if (Number.isNaN(num)) return ''
        if (num === 0 && !acceptZero) return ''

        // eslint-disable-next-line no-undef
        const locale = marketCountry === US_COUNTRY ? 'en-US' : LANGUAGE_CODE
        return num.toLocaleString(locale, {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        })
      },

      /**
       * Converts locale string to number
       *
       *  • «100 000,50» → 100000.5   (fr, ru, da …)
       *  • «100,000.50» → 100000.5   (en, zh …)
       *  • «100.000,50» → 100000.5   (de, es …)
       *  • «100,00»     → 100        (without a thousand separators)
       */
      parseNumber (value) {
        if (value === null || value === undefined || value === '') return NaN
        if (typeof value === 'number') return value

        // 1) remove non-printable symbols
        let s = String(value).replace(/[\u00A0\u202F\s]/g, '').trim()

        // 2) detect the decimal part
        const lastComma = s.lastIndexOf(',')
        const lastDot   = s.lastIndexOf('.')
        const decimalSep = lastComma > lastDot ? ',' :
                           lastDot   > lastComma ? '.' : ''

        // 3) Remove a thousand separator
        if (decimalSep) {
          const thousandSep = decimalSep === ',' ? '\\.' : ','
          s = s.replace(new RegExp(thousandSep, 'g'), '')
        }

        // 4) Decimal to dot
        if (decimalSep && decimalSep !== '.') {
          s = s.replace(decimalSep, '.')
        }

        // 5) allow to use only eligible for parseFloat symbols
        s = s.replace(/[^0-9.+-Ee]/g, '')

        return parseFloat(s)
      },

      /**
       * parseNumber + empty fallback. Used by locale-aware v-model proxies
       * that need to store canonical dot-decimal (or empty) without ever
       * leaking NaN back into the form state.
       */
      parseNumberOrEmpty (value) {
        const n = this.parseNumber(value)
        return Number.isNaN(n) ? '' : String(n)
      },

      /**
       * `@beforeinput.native` handler for decimal text inputs. Blocks
       * letters and symbols on both typing and paste (`type="text"
       * inputmode="decimal"` constrains the mobile keyboard but desktop
       * browsers still accept any character).
       */
      blockNonDecimalInput (event) {
        if (event.data === null) return  // backspace / arrow / delete: allow
        if (!/^[0-9.,+-]+$/.test(event.data)) event.preventDefault()
      },

      formatVatGroupLabel (item) {
        if (!item.vatGroupName) return '-'
        if (item.vatGroupPercent === null || item.vatGroupPercent === undefined) return item.vatGroupName
        return `${item.vatGroupName} (${this.formatNumber(item.vatGroupPercent)}%)`
      },

      formatMoney (value, market, options = {}) {
        const currency = (market && market.currencyCode) || ''
        const formatted = this.formatNumber(value, {
          marketCountry: market && market.supportCountry && market.supportCountry.code,
          ...options,
        })
        return formatted ? `${formatted} ${currency}`.trim() : ''
      },

      /**
       * Format date based on market locale
       * US: MM/DD/YYYY
       * EU: DD-MM-YYYY or DD.MM.YYYY
       * @param {string|Date} rawDate - Date to format
       * @param {string} marketCountry - Market country code
       * @param {string} marketTimezone - Market timezone (optional, only used if rawDate has time component)
       */
      formatDate (rawDate, marketCountry = null, marketTimezone = null) {
        if (!rawDate) return ''

        const format = marketCountry === US_COUNTRY ? 'MM/DD/YYYY' : getDateFormat(marketCountry)

        const m = moment(rawDate)

        if (marketTimezone && m.format('HH:mm:ss') !== '00:00:00') {
          return m.tz(marketTimezone).format(format)
        }

        return m.format(format)
      },

      /**
       * Format datetime based on market locale with timezone conversion
       * US: MM/DD/YYYY hh:mm A (12-hour with AM/PM)
       * EU: DD-MM-YYYY HH:mm (24-hour)
       * @param {string|Date} rawDateTime - DateTime to format
       * @param {string} marketCountry - Market country code
       * @param {string} marketTimezone - Market timezone (default: Europe/Berlin)
       */
      formatDateTime (rawDateTime, marketCountry = null, marketTimezone = DEFAULT_TIMEZONE) {
        if (!rawDateTime) return ''

        const format = marketCountry === US_COUNTRY ? 'MM/DD/YYYY hh:mm A' : getDateTimeFormat(marketCountry)

        return moment(rawDateTime)
          .tz(marketTimezone || DEFAULT_TIMEZONE)
          .format(format)
      },

      /**
       * Format time based on market locale
       * US: hh:mm A (12-hour with AM/PM)
       * EU: HH:mm (24-hour)
       * @param {string} rawTime - Time to format (HH:mm format)
       * @param {string} marketCountry - Market country code
       */
      formatTime (rawTime, marketCountry = null) {
        if (!rawTime) return ''

        const usFormat = 'hh:mm A'
        const euFormat = 'HH:mm'
        const format = marketCountry === US_COUNTRY ? usFormat : euFormat

        return moment(rawTime, 'HH:mm').format(format)
      },
    }
  }
</script>
